/* eslint-disable max-lines */
import {
    CalendarIcon,
    CogIcon,
    ExclamationCircleIcon,
    ExclamationIcon,
    FlagIcon,
    LightBulbIcon,
    LockClosedIcon,
    LockOpenIcon,
    PauseIcon,
    PlayIcon,
    UserIcon,
    VideoCameraIcon,
    XCircleIcon,
} from '../../Icon'
import {
    InfoMessageProps,
    MessageProps,
} from '../types'
import Attribute from './Attribute'
import Highlight from '../../Highlight'
import InfoTransferMessage from './InfoTransferMessage'
import {NinchatMessages} from 'api/types'
import React from 'react'
import {useTranslation} from 'react-i18next'

const InfoMessageView = (props: InfoMessageProps): React.ReactElement => {
    const {message} = props

    const {t} = useTranslation()

    const getInfoChannelClosed = (m: MessageProps): React.ReactElement => (
        <>
            {!!m.channel_attrs_old && !!m.channel_attrs_old.audience_id &&
                <Attribute
                    icon={FlagIcon}
                    renderAsMarkdown={true}
                    text={t('Conversation ended')}
                    type='success'
                />}

            {(!m.channel_attrs_old || !m.channel_attrs_old.audience_id) &&
                <Attribute
                    icon={m.cause === 'channel_schedule' ? CalendarIcon : PauseIcon}
                    text={t('Channel is now closed')}
                    type='error'
                />}
        </>
    )

    const getInfoChannelSuspended = (): React.ReactElement => (
        <Attribute
            icon={XCircleIcon}
            text={t('Channel is now suspended')}
            type='error'
        />)

    const getInfoChannelNotSuspended = (): React.ReactElement => (
        <Attribute
            icon={ExclamationCircleIcon}
            text={t('Channel is no longer suspended')}
            type='success'
        />)

    const getInfoChannelOpen = (m: MessageProps): React.ReactElement => (
        <Attribute
            icon={m.cause === 'channel_schedule' ? CalendarIcon : PlayIcon}
            text={t('Channel is now open')}
            type='success'
        />
    )

    const getInfoCustom = (m: MessageProps): React.ReactElement => (
        <Attribute
            icon={PlayIcon}
            renderAsMarkdown={m.renderAsMarkdown}
            text={m.customText ?? ''}
        />
    )

    const getInfoChannelVideo = (m: MessageProps): React.ReactElement => {
        if (m.channel_attrs_new && m.channel_attrs_new.video === 'group') {
            return (
                <Attribute
                    icon={VideoCameraIcon}
                    text={t('Channel is now a video meeting')}
                    type='success'
                />
            )
        }

        return (
            <Attribute
                icon={VideoCameraIcon}
                text={t('Channel is no longer a video meeting')}
                type='error'
            />
        )
    }

    const getInfoChannelName = (m: MessageProps): React.ReactElement => (
        // TODO: Handle channel name removed?
        <Attribute
            icon={CogIcon}
            text={t('Channel name changed to {{name}}', {name: m.channel_attrs_new?.name})}
        />
    )

    const getInfoChannelTopic = (m: MessageProps): React.ReactElement => (
        // TODO: Handle channel topic removed?
        <>
            <Attribute
                icon={LightBulbIcon}
                text={t('Channel topic changed to:')}
            />
            <Highlight>{String(m.channel_attrs_new?.topic)}</Highlight>
        </>
    )

    const getInfoChannelPublic = (): React.ReactElement => (
        <Attribute
            icon={LockOpenIcon}
            text={t('Channel is now public. Anyone can join.')}
        />
    )

    const getInfoChannelPrivate = (): React.ReactElement => (
        <Attribute
            icon={LockClosedIcon}
            text={t('Channel is now private. Only invited users can join.')}
        />
    )

    const getInfoChannelFollowable = (followable: boolean): React.ReactElement => (
        followable ?
            <Attribute
                icon={ExclamationIcon}
                text={t('Channel is now followable. Anyone can follow conversation without joining.')}
            />
            :
            <Attribute
                icon={ExclamationIcon}
                text={t('Channel is no longer followable. Only channel members can follow conversation.')}
            />
    )

    const getBlackListedMessage = (blacklisted_message_types: NinchatMessages.NinchatMessageType[]): React.ReactElement => (
        blacklisted_message_types === null ?
            <Attribute
                icon={LockClosedIcon}
                text={t('Channel is now displaying join and part messages')}
                type='success'
            />
            :
            <Attribute
                icon={LockClosedIcon}
                text={t('Channel no longer displays join and part messages')}
                type='error'
            />
    )

    // eslint-disable-next-line complexity
    const getInfoChannelMessage = (m: MessageProps): React.ReactElement => (
        <>
            {!!m.channel_attrs_new && !!m.channel_attrs_new.closed &&
                getInfoChannelClosed(m)}

            {!!m.channel_attrs_new && m.channel_attrs_new.closed === false &&
                getInfoChannelOpen(m)}

            {!!m.channel_attrs_new && m.channel_attrs_new.suspended === false &&
                getInfoChannelNotSuspended()}

            {!!m.channel_attrs_new && !!m.channel_attrs_new.suspended &&
                getInfoChannelSuspended()}

            {!!m.channel_attrs_new && 'video' in m.channel_attrs_new &&
                getInfoChannelVideo(m)}

            {!!m.channel_attrs_new && !!m.channel_attrs_new.name &&
                getInfoChannelName(m)}

            {!!m.channel_attrs_new && !!m.channel_attrs_new.topic &&
                getInfoChannelTopic(m)}

            {!!m.channel_attrs_new && !!m.channel_attrs_new.public &&
                getInfoChannelPublic()}

            {!!m.channel_attrs_new && !!m.channel_attrs_new.private &&
                getInfoChannelPrivate()}

            {!!m.channel_attrs_new && m.channel_attrs_new.followable !== undefined &&
                getInfoChannelFollowable(!!m.channel_attrs_new.followable)}

            {!!m.channel_attrs_new &&
                m.channel_attrs_new.blacklisted_message_types !== undefined &&
                getBlackListedMessage(m.channel_attrs_new.blacklisted_message_types)}

            {!!m.customText &&
                getInfoCustom(m)}
        </>
    )

    const getInfoUserMessage = (m: MessageProps): React.ReactElement => (
        <>
            {!!m.user_name && !!m.user_name_old &&
                <Attribute
                    icon={UserIcon}
                    text={t('{{user_name_old}} is now known as {{user_name}}', {
                        user_name: m.user_name,
                        user_name_old: m.user_name_old,
                    })}
                />}

            {!!m.user_name && !m.user_name_old &&
                <Attribute
                    icon={UserIcon}
                    text={t('Unnamed user is now known as {{user_name}}', {user_name: m.user_name})}
                />}

            {!m.user_name && !!m.user_name_old &&
                <Attribute
                    icon={UserIcon}
                    text={t('{{user_name_old}} is now unnamed', {user_name_old: m.user_name_old})}
                />}

            {!!m.user_deleted &&
                <Attribute
                    icon={UserIcon}
                    text={t('User account deleted')}
                />}
        </>
    )

    const getInfoMessage = (m: MessageProps): React.ReactElement => {
        switch (m.message_type) {
            case 'ninchat.com/info/channel':
                return getInfoChannelMessage(m)
            case 'ninchat.com/info/user':
                return getInfoUserMessage(m)
            case 'ninchat.com/info/join':
                return (
                    <>
                        {!!m.user_name &&
                            <Attribute
                                icon={UserIcon}
                                text={t('{{user_name}} joined conversation', {user_name: m.user_name})}
                                type='success'
                            />}
                        {!m.user_name &&
                            <Attribute
                                icon={UserIcon}
                                text={t('Unnamed user joined conversation')}
                                type='success'
                            />}
                    </>
                )
            case 'ninchat.com/info/part':
                return (
                    <>
                        {!!m.user_name &&
                            <Attribute
                                icon={UserIcon}
                                text={t('{{user_name}} left conversation', {user_name: m.user_name})}
                            />}
                        {!m.user_name &&
                            <Attribute
                                icon={UserIcon}
                                text={t('Unnamed user left conversation')}
                            />}
                    </>
                )
            case 'ninchat.com/info/transfer/source':
            case 'ninchat.com/info/transfer/target':
                return <InfoTransferMessage {...m} />
            case 'ninchat.com/info/member':
                if (m.member_invitee === false) {
                    return (
                        <Attribute
                            icon={UserIcon}
                            text={t('{{user_name}} is now present', {user_name: m.user_name})}
                        />
                    )
                }

                if (m.member_silenced === true || m.member_silenced === false) {
                    return (
                        <Attribute
                            icon={UserIcon}
                            text={m.member_silenced ? t('{{user_name}} got silenced', {user_name: m.user_name}) : t('{{user_name}} got unsilenced', {user_name: m.user_name})}
                        />
                    )
                }
                // eslint-disable-next-line react/jsx-no-useless-fragment
                return <></>
            default:
        }

        return (
            <Attribute
                icon={CogIcon}
                json={true}
                text={JSON.stringify(m)}
            />
        )
    }

    return getInfoMessage(message)
}

export default InfoMessageView
